import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { Typography, type TypographySize } from '../typography'
import type { CSSVariableColor } from '../../types/colors'
import { Col } from '../col'

const alertVariants = cva('relative px-md w-full border rounded-sm', {
  variants: {
    size: {
      sm: 'py-2xs',
      md: 'py-xs',
    },
    color: {
      base: 'bg-base-surface  border-base-surface-overlays-5 [&_svg]:fill-base-surface-100',
      neutral:
        'bg-neutral-surface-light border-neutral-surface-overlays-5 [&_svg]:fill-neutral-surface-100',
      positive:
        'bg-positive-surface-light  border-positive-surface-overlays-5 [&_svg]:fill-positive-surface-100',
      negative:
        'bg-negative-surface-light  border-negative-surface-overlays-5 [&_svg]:fill-negative-surface-100',
      alert:
        'bg-alert-surface-light  border-alert-surface-overlays-5 [&_svg]:fill-alert-surface-100',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'base',
  },
})

const defaultIcons: Record<string, React.ReactNode> = {
  base: <Icon name='UndefinedGhost' />,
  positive: <Icon name='UndefinedGhost' />,
  negative: <Icon name='UndefinedGhost' />,
  alert: <Icon name='UndefinedGhost' />,
  neutral: <Icon name='UndefinedGhost' />,
}

const titleSizeMap: Record<string, TypographySize> = {
  sm: '2xs',
  md: 'xs',
}

const titleColorMap: Record<string, CSSVariableColor> = {
  base: 'text-base-surface-80',
  neutral: 'text-neutral-surface-80',
  positive: 'text-positive-surface-80',
  negative: 'text-negative-surface-80',
  alert: 'text-alert-surface-80',
}

const captionSizeMap: Record<string, TypographySize> = {
  sm: 'xs',
  md: 'sm',
}

const captionColorMap: Record<string, CSSVariableColor> = {
  base: 'text-base-surface-100',
  neutral: 'text-neutral-surface-100',
  positive: 'text-positive-surface-100',
  negative: 'text-negative-surface-100',
  alert: 'text-alert-surface-100',
}
const iconSizeMap = {
  sm: '[&_svg]:size-icon-xs',
  md: '[&_svg]:size-icon-sm',
}

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color' | 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode
  icon?: React.ReactNode
  hideIcon?: boolean
  caption?: React.ReactNode
  children?: React.ReactNode
}

export function Alert({
  className,
  size = 'md',
  color = 'base',
  title,
  icon,
  hideIcon = false,
  caption,
  children,
  ...props
}: AlertProps) {
  const colorKey = color ?? 'base'
  const IconToShow = icon ?? defaultIcons[colorKey as string] ?? null
  const sizeKey = size ?? 'md'

  return (
    <div
      role='alert'
      className={cn(
        alertVariants({ size, color }),
        'flex items-center gap-md',
        className,
      )}
      {...props}
    >
      {IconToShow && !hideIcon && (
        <span
          className={cn(
            'flex items-center justify-center h-full',
            iconSizeMap[sizeKey],
          )}
        >
          {IconToShow}
        </span>
      )}
      <Col>
        {title && (
          <Typography
            variant='p'
            size={titleSizeMap[sizeKey]}
            color={titleColorMap[colorKey]}
            weight='bold'
          >
            {title}
          </Typography>
        )}
        {caption && (
          <Typography
            variant='p'
            size={captionSizeMap[sizeKey]}
            color={captionColorMap[colorKey]}
            weight='regular'
          >
            {caption}
          </Typography>
        )}
        {children && <Col className='py-xs'>{children}</Col>}
      </Col>
    </div>
  )
}
